import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBed } from '@fortawesome/pro-regular-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Subscription,
} from 'rxjs';
import { StaysKpiHelperService } from '../../../services/stays-kpi-helper.service';
import { StaysDrawerStateService } from '@wilson/non-domain-specific/stays-form-and-drawer';
import { StaysKpiDrawerListComponent } from '../stays-kpi-drawer-list/stays-kpi-drawer-list.component';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';

@Component({
  selector: 'wilson-stays-kpi',
  templateUrl: './stays-kpi.component.html',
  styleUrls: ['./stays-kpi.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    NzSkeletonModule,
    StaysKpiDrawerListComponent,
    NzDrawerModule,
  ],
  providers: [StaysKpiHelperService, StaysDrawerStateService],
})
export class StaysKpiComponent implements OnInit, OnDestroy {
  readonly staysKpiHelperService = inject(StaysKpiHelperService);
  readonly shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  readonly staysDrawerStateService = inject(StaysDrawerStateService);
  private readonly store = inject(Store);
  readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  faBed = faBed;
  subscription = new Subscription();
  isDrawerVisible = false;

  ngOnInit(): void {
    const visibleDateRange$ = this.store.select(
      ShiftTimelineDataState.visibleDateRange,
    );
    const filteredUsers$ =
      this.shiftTimelineFilterStateService.filteredUsers$.pipe(
        debounceTime(700),
      );
    const currentDate$ = this.store.select(ShiftTimelineDataState.currentDate);
    const isCreateStaysDrawerVisible$ =
      this.staysDrawerStateService.isCreateStaysDrawerVisible$;
    const isEditStaysDrawerVisible$ =
      this.staysDrawerStateService.isEditStaysDrawerVisible$;

    const refreshKPI$ = this.timelineStaysSuggestionService.refreshKPI$;

    this.subscription.add(
      combineLatest([
        visibleDateRange$,
        currentDate$,
        filteredUsers$,
        isCreateStaysDrawerVisible$,
        isEditStaysDrawerVisible$,
        refreshKPI$,
      ])
        .pipe(
          debounceTime(500),
          distinctUntilChanged(
            (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
          ),
        )
        .subscribe(
          ([
            visibleDateRange,
            currentDate,
            filteredUsers,
            isCreateStaysDrawerVisible,
            isEditStaysDrawerVisible,
          ]) => {
            if (
              (!isCreateStaysDrawerVisible || !isEditStaysDrawerVisible) &&
              visibleDateRange.start &&
              visibleDateRange.end
            ) {
              const userIds = filteredUsers.map((user) => user.id as string);
              this.staysKpiHelperService.setStayCounterForTimeframe(
                {
                  start: visibleDateRange.start,
                  end: visibleDateRange.end,
                },
                currentDate || new Date(visibleDateRange.start).toISOString(),
                userIds,
              );
            }
          },
        ),
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
